// src/pages/Shared/Settings.jsx — works for buyer, seller, admin
import React, { useState } from 'react';
import {
  Container, Paper, Typography, Box, TextField, Button,
  Divider, Alert, CircularProgress, Switch, FormControlLabel,
} from '@mui/material';
import { Lock, Notifications, Security, Save } from '@mui/icons-material';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

const Section = ({ icon, title, children }) => (
  <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
    <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon} {title}
    </Typography>
    {children}
  </Paper>
);

const Settings = () => {
  const [pwData,   setPwData]   = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwError,  setPwError]  = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const [notifs, setNotifs] = useState({ email_enquiry: true, email_invoice: true, email_shipment: true });

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isOAuth    = !!storedUser.oauth_provider;

  const handlePasswordChange = async () => {
    setPwError('');
    if (!pwData.current_password || !pwData.new_password || !pwData.confirm_password) {
      setPwError('All fields are required'); return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      setPwError('New passwords do not match'); return;
    }
    if (pwData.new_password.length < 8) {
      setPwError('Password must be at least 8 characters'); return;
    }
    try {
      setPwSaving(true);
      await authAPI.changePassword({ current_password: pwData.current_password, new_password: pwData.new_password });
      setPwData({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password changed successfully!');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 4 }}>Account Settings</Typography>

      {/* Change Password */}
      <Section icon={<Lock color="primary" />} title="Change Password">
        {isOAuth ? (
          <Alert severity="info">
            You signed in with <strong>{storedUser.oauth_provider}</strong>. Password change is not available for OAuth accounts.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
            {pwError && <Alert severity="error">{pwError}</Alert>}
            <TextField
              label="Current Password" type="password" fullWidth
              value={pwData.current_password}
              onChange={e => setPwData({ ...pwData, current_password: e.target.value })}
              autoComplete="current-password"
            />
            <TextField
              label="New Password" type="password" fullWidth
              value={pwData.new_password}
              onChange={e => setPwData({ ...pwData, new_password: e.target.value })}
              helperText="At least 8 characters"
              autoComplete="new-password"
            />
            <TextField
              label="Confirm New Password" type="password" fullWidth
              value={pwData.confirm_password}
              onChange={e => setPwData({ ...pwData, confirm_password: e.target.value })}
              autoComplete="new-password"
            />
            <Button
              variant="contained" sx={{ alignSelf: 'flex-start', px: 4 }}
              startIcon={pwSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handlePasswordChange} disabled={pwSaving}>
              {pwSaving ? 'Updating...' : 'Update Password'}
            </Button>
          </Box>
        )}
      </Section>

      {/* Notifications */}
      <Section icon={<Notifications color="primary" />} title="Email Notifications">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            { key: 'email_enquiry',  label: 'New enquiry updates',   desc: 'Get notified when your enquiry status changes' },
            { key: 'email_invoice',  label: 'Invoice notifications',  desc: 'Receive invoices and payment confirmations' },
            { key: 'email_shipment', label: 'Shipment tracking',      desc: 'Updates on your shipment status' },
          ].map(({ key, label, desc }) => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Box>
                <Typography variant="body1" fontWeight={600}>{label}</Typography>
                <Typography variant="body2" color="text.secondary">{desc}</Typography>
              </Box>
              <Switch checked={notifs[key]} onChange={e => setNotifs({ ...notifs, [key]: e.target.checked })} color="primary" />
            </Box>
          ))}
          <Button variant="outlined" sx={{ alignSelf: 'flex-start', mt: 1 }}
            onClick={() => toast.success('Notification preferences saved!')}>
            Save Preferences
          </Button>
        </Box>
      </Section>

      {/* Account Info */}
      <Section icon={<Security color="primary" />} title="Account Information">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            { label: 'Email',          value: storedUser.email || '—' },
            { label: 'Account Type',   value: (storedUser.user_type || '—').toUpperCase() },
            { label: 'Login Method',   value: storedUser.oauth_provider ? `Google (${storedUser.oauth_provider})` : 'Email & Password' },
            { label: 'Account Status', value: storedUser.verified ? 'Verified ✅' : 'Pending Verification ⏳' },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
              <Typography variant="body2" fontWeight={500}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Section>
    </Container>
  );
};

export default Settings;
